import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDataProvider, useGetList } from 'react-admin';
import { TextField, Select, MenuItem, FormControl, InputLabel, Paper, Typography, Grid, Alert, CircularProgress, Box, FormHelperText, Autocomplete, Chip } from '@mui/material';
import { z } from 'zod';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { EmployeeSearchAutocomplete } from '@/components/employee/EmployeeSearchAutocomplete';
import type { EmployeeSearchResult } from '@/components/employee';
import { buildTransitionChain, useRuleEvaluation } from '@/components/workflow';
import { stepValidator } from '@/components/wizard/validateWithZod';

interface PrefilledField {
  field: string;
  label: string;
  sourceValue: unknown;
  currentValue: unknown;
  confirmed: boolean;
}

const STEPS = [
  { id: 'details', title: 'Training Details' },
  { id: 'seta', title: 'SETA Fields' },
  { id: 'funding', title: 'Funding' },
  { id: 'review', title: 'Review & Submit' },
];

const INTERVENTION_TYPES = ['Skills Programme', 'Learnership', 'Short Course', 'Mentorship', 'Workshop', 'Certification'];
const FUNDING_SOURCES = ['Company Funded', 'SETA Grant', 'Bursary', 'Learnership'];

interface FormValues {
  employeeName: string;
  employeeId: string;
  trainingProviderId: string;
  trainingProviderName: string;
  courseId: string;
  courseName: string;
  startDate: string;
  endDate: string;
  durationHours: number;
  cost: number;
  ofocCode: string;
  interventionType: string;
  fundingSource: string;
  description: string;
}

const INITIAL_VALUES: FormValues = {
  employeeName: '',
  employeeId: '',
  trainingProviderId: '',
  trainingProviderName: '',
  courseId: '',
  courseName: '',
  startDate: '',
  endDate: '',
  durationHours: 0,
  cost: 0,
  ofocCode: '',
  interventionType: '',
  fundingSource: '',
  description: '',
};

const stepSchemas = {
  details: z.object({
    employeeName: z.string().trim().min(1, 'Employee name is required'),
    trainingProviderId: z.string().min(1, 'Training provider is required'),
    courseId: z.string().min(1, 'Course is required'),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    durationHours: z.number().gt(0, 'Duration must be greater than 0'),
  }),
  seta: z.object({
    ofocCode: z.string().trim().min(1, 'OFOC code is required'),
    interventionType: z.string().min(1, 'Intervention type is required'),
  }),
  funding: z.object({
    cost: z.number().gt(0, 'Cost must be greater than 0'),
    fundingSource: z.string().min(1, 'Funding source is required'),
  }),
};

function validateStep(stepId: string, values: FormValues): { isValid: boolean; errors: Record<string, string> } {
  const schema = stepSchemas[stepId as keyof typeof stepSchemas];
  if (!schema) return { isValid: true, errors: {} };

  const baseResult = stepValidator(schema)(values as unknown as Record<string, unknown>);
  if (!baseResult.isValid) return baseResult;

  if (stepId === 'details' && values.startDate && values.endDate) {
    const start = new Date(values.startDate);
    const end = new Date(values.endDate);
    if (end < start) {
      return { isValid: false, errors: { endDate: 'End date must be after start date' } };
    }
  }

  return baseResult;
}

export const TrainingWizard = () => {
  const { employeeId } = useParams();
  const dataProvider = useDataProvider();

  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSearchResult | null>(null);
  const [prefilledFields, setPrefilledFields] = useState<PrefilledField[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [ofoCodeList, setOfoCodeList] = useState<Array<{ ofoCode: string; description: string }>>([]);

  useEffect(() => {
    fetch('/data/ofo-codes.json')
      .then(r => r.json())
      .then(data => setOfoCodeList(data))
      .catch(() => {});
  }, []);

  const { data: allProviders } = useGetList('training-providers', { pagination: { page: 1, perPage: 100 }, sort: { field: 'providerName', order: 'ASC' } });
  const { data: availableCourses } = useGetList('courses', {
    filter: selectedProviderId ? { 'trainingProvider.id': selectedProviderId } : { status: 'Active' },
    pagination: { page: 1, perPage: 100 },
  });

  const activeProviders = allProviders?.filter(p => p.status === 'Active' && p.accreditationStatus === 'Active') || [];

  const handleProviderChange = (providerId: string) => {
    setSelectedProviderId(providerId);
    const provider = activeProviders.find(p => p.id === providerId);
    updateValues({
      trainingProviderId: providerId,
      trainingProviderName: provider?.providerName || '',
      courseId: '',
      courseName: '',
    });
  };

  const handleCourseChange = (courseId: string) => {
    const course = availableCourses?.find(c => c.id === courseId);
    if (course) {
      updateValues({
        courseId: courseId,
        courseName: course.courseTitle,
        durationHours: course.durationHours,
        ofocCode: course.ofocodes?.[0]?.id || '',
      });
    } else {
      updateValues({ courseId: courseId, courseName: '' });
    }
  };

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    const context: Record<string, unknown> = {
      trainingId: null,
      employeeId: v.employeeId,
      courseName: v.courseName,
      trainingProviderId: v.trainingProviderId,
      ofoCode: v.ofocCode,
      interventionType: v.interventionType,
      nqfLevel: null,
      creditValue: null,
      cost: v.cost,
      startDate: v.startDate,
      endDate: v.endDate,
      durationHours: v.durationHours,
      certificateReference: null,
      employeeRace: null,
      employeeGender: null,
      employeeDisability: null,
      bbbeeSpendRecorded: false,
      status: null,
    };

    const trainingResult = await dataProvider.create('training-records', { data: formValues });
    const trainingId = trainingResult.data.id;

    const wizardEvents = ['SUBMIT_FOR_APPROVAL', 'APPROVE', 'COMPLETE'];
    const { finalState, transitions } = buildTransitionChain('training', 'planned', wizardEvents);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'training',
        entityId: trainingId,
        entityName: 'Training: ' + v.courseName + ' - ' + v.employeeName,
        currentState: { value: finalState, context },
        status: 'active',
        workflowVersion: '1.0',
        startedAt: new Date().toISOString(),
        lastActionAt: new Date().toISOString(),
        pausedAt: null,
        breakpointId: null,
        relatedEntityId: trainingId,
        transitions,
      },
    });

    return { id: trainingId };
  }, [dataProvider]);

  const wizard = useWizard({
    steps: STEPS,
    initialValues: {
      ...INITIAL_VALUES,
      employeeId: employeeId || '',
    },
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    isSaving,
    isCompleted,
    progress,
    values,
    errors,
    stepFeedback,
    createdId,
    nextStep,
    prevStep,
    updateValues,
    validateStep: wizardValidateStep,
    complete,
    reset,
    saveDraft,
  } = wizard;

  const { evaluate: evaluateRule, isEvaluating: isEvaluatingRule, result: ruleResult } = useRuleEvaluation({
    ruleId: 'training-guards',
    context: {
      entityId: createdId || '',
      entityType: 'process',
      currentState: currentStep?.id || 'initiated',
      event: 'SUBMIT',
      employeeId: (values as unknown as FormValues).employeeId,
      courseName: (values as unknown as FormValues).courseName,
      cost: (values as unknown as FormValues).cost,
      fundingSource: (values as unknown as FormValues).fundingSource,
    },
  });

  useEffect(() => {
    if (currentStep?.id === 'review') {
      evaluateRule();
    }
  }, [currentStep?.id, evaluateRule]);

  const vals = values as unknown as FormValues;

  const localUpdateValues = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value as unknown });
  };

  const handleEmployeeSelect = (employee: EmployeeSearchResult) => {
    setSelectedEmployee(employee);
    const fields: PrefilledField[] = [
      { field: 'department', label: 'Department', sourceValue: employee.organisationalHierarchy?.department || '-', currentValue: '', confirmed: false },
    ];
    setPrefilledFields(fields);
    updateValues({
      employeeName: employee.fullName,
      employeeId: employee.employeeNumber,
    });
  };

  const handleClearEmployee = () => {
    setSelectedEmployee(null);
    setPrefilledFields([]);
  };

  const handleNext = () => {
    const valid = wizardValidateStep((v) => validateStep(currentStep.id, v as unknown as FormValues));
    if (!valid) return;
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const renderFormFields = (stepId: string): React.ReactNode => {
    switch (stepId) {
      case 'details':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <EmployeeSearchAutocomplete
                label="Search Employee"
                onEmployeeSelect={handleEmployeeSelect}
                onClear={handleClearEmployee}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Employee Name"
                value={vals.employeeName}
                onChange={e => localUpdateValues('employeeName', e.target.value)}
                fullWidth
                error={!!errors.employeeName}
                helperText={errors.employeeName}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Employee #"
                value={vals.employeeId}
                onChange={e => localUpdateValues('employeeId', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.trainingProviderId}>
                <InputLabel>Training Provider *</InputLabel>
                <Select
                  value={vals.trainingProviderId}
                  label="Training Provider *"
                  onChange={e => handleProviderChange(e.target.value as string)}
                >
                  {activeProviders.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      <Box>
                        <Typography variant="body2">{p.providerName}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.setaAccreditationNumber} · {p.accreditationExpiry}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.trainingProviderId && <FormHelperText>{errors.trainingProviderId}</FormHelperText>}
                {!errors.trainingProviderId && <FormHelperText>Select an accredited training provider</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.courseId} disabled={!vals.trainingProviderId}>
                <InputLabel>Course *</InputLabel>
                <Select
                  value={vals.courseId}
                  label="Course *"
                  onChange={e => handleCourseChange(e.target.value as string)}
                >
                  {(availableCourses || []).map(c => (
                    <MenuItem key={c.id} value={c.id}>
                      <Box>
                        <Typography variant="body2">{c.courseTitle}</Typography>
                        <Typography variant="caption" color="text.secondary">NQF {c.nqfLevel} · {c.creditValue} credits · {c.durationHours}h · {c.deliveryMode}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.courseId && <FormHelperText>{errors.courseId}</FormHelperText>}
                {!errors.courseId && !vals.trainingProviderId && <FormHelperText>Select provider first</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Duration (Hours)"
                type="number"
                value={vals.durationHours}
                onChange={e => localUpdateValues('durationHours', parseInt(e.target.value) || 0)}
                fullWidth
                error={!!errors.durationHours}
                helperText={errors.durationHours || (vals.courseName ? `From course: ${vals.courseName}` : 'Auto-filled from course selection')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Start Date"
                type="date"
                value={vals.startDate}
                onChange={e => localUpdateValues('startDate', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.startDate}
                helperText={errors.startDate}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="End Date"
                type="date"
                value={vals.endDate}
                onChange={e => localUpdateValues('endDate', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.endDate}
                helperText={errors.endDate}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                value={vals.description}
                onChange={e => localUpdateValues('description', e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Brief description of training intervention"
              />
            </Grid>
            {vals.courseId && ofoCodeList.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" icon={false} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    OFO codes linked to this course (will be used on SETA step):
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(availableCourses?.find(c => c.id === vals.courseId)?.ofocodes || []).map((ofo: { id: string; type?: string }) => {
                      const match = ofoCodeList.find(o => o.ofoCode === ofo.id);
                      return (
                        <Chip
                          key={ofo.id}
                          label={`${ofo.id}${match ? ` — ${match.description}` : ''}`}
                          size="small"
                          variant="outlined"
                          color={ofo.id === vals.ofocCode ? 'primary' : 'default'}
                        />
                      );
                    })}
                  </Box>
                </Alert>
              </Grid>
            )}
          </Grid>
        );

      case 'seta': {
        const selectedOfo = ofoCodeList.find(o => o.ofoCode === vals.ofocCode);
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={ofoCodeList}
                getOptionLabel={(o) => `${o.ofoCode} — ${o.description}`}
                value={selectedOfo || null}
                onChange={(_, newVal) => localUpdateValues('ofocCode', newVal?.ofoCode || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="OFOC Code *"
                    error={!!errors.ofocCode}
                    helperText={errors.ofocCode || (selectedOfo ? `${selectedOfo.description}` : vals.courseName ? 'Auto-populated from course' : 'Select OFO code')}
                  />
                )}
                fullWidth
                noOptionsText="No matching OFO codes found"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.interventionType}>
                <InputLabel>Intervention Type</InputLabel>
                <Select
                  value={vals.interventionType}
                  label="Intervention Type"
                  onChange={e => localUpdateValues('interventionType', e.target.value)}
                >
                  {INTERVENTION_TYPES.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {vals.courseName && vals.ofocCode && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="success" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>OFO code auto-populated from course: <strong>{vals.courseName}</strong></span>
                  </Box>
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                OFOC codes are mandatory for SETA grant claims. Ensure the code matches the learning programme registered with the relevant SETA.
              </Alert>
            </Grid>
          </Grid>
        );
      }

      case 'funding':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Total Cost (ZAR)"
                type="number"
                value={vals.cost}
                onChange={e => localUpdateValues('cost', parseFloat(e.target.value) || 0)}
                fullWidth
                error={!!errors.cost}
                helperText={errors.cost || 'Total training cost in Rand'}
                InputProps={{ startAdornment: 'R ' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.fundingSource}>
                <InputLabel>Funding Source</InputLabel>
                <Select
                  value={vals.fundingSource}
                  label="Funding Source"
                  onChange={e => localUpdateValues('fundingSource', e.target.value)}
                >
                  {FUNDING_SOURCES.map(source => (
                    <MenuItem key={source} value={source}>{source}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert severity="success">
                Training spend categorization: Company Funded and SETA Grant expenditure qualifies for B-BBEE skills development points under the Skills Development Act.
              </Alert>
            </Grid>
          </Grid>
        );

      case 'review':
        return (
          <Grid container spacing={2}>
            {ruleResult && !ruleResult.allowed && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Guard Check</Typography>
                  <Typography variant="body2">{ruleResult.reason}</Typography>
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Employee Information</Typography>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Employee Name</Typography>
                    <Typography variant="body2">{vals.employeeName || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Employee #</Typography>
                    <Typography variant="body2">{vals.employeeId || '-'}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Training Details</Typography>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Course</Typography>
                    <Typography variant="body2">{vals.courseName || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Provider</Typography>
                    <Typography variant="body2">{vals.trainingProviderName || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="caption" color="text.secondary">Start Date</Typography>
                    <Typography variant="body2">{vals.startDate || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="caption" color="text.secondary">End Date</Typography>
                    <Typography variant="body2">{vals.endDate || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                    <Typography variant="body2">{vals.durationHours} hours</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>SETA & Funding</Typography>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">OFOC Code</Typography>
                    <Typography variant="body2">{vals.ofocCode || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Intervention Type</Typography>
                    <Typography variant="body2">{vals.interventionType || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Cost</Typography>
                    <Typography variant="body2">R {vals.cost.toLocaleString()}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Funding Source</Typography>
                    <Typography variant="body2">{vals.fundingSource || '-'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  if (isCompleted) {
    return (
      <WizardLayout
        steps={STEPS}
        currentStepIndex={currentStepIndex}
        progress={progress}
        isSaving={isSaving}
        isCompleted={isCompleted}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        onReset={reset}
        footerActions={[
          { label: 'View Training Records', to: '/training' },
          { label: 'Start Another', onClick: reset },
        ]}
      >
        <OutcomeSummary
          title="Training Intervention Created"
          processId={createdId || undefined}
          fields={[
            { label: 'Employee', value: vals.employeeName },
            { label: 'Cost', value: `R ${vals.cost.toLocaleString()}` },
            { label: 'Funding Source', value: vals.fundingSource },
            { label: 'OFOC Code', value: vals.ofocCode },
            { label: 'Intervention Type', value: vals.interventionType },
          ]}
          footerActions={[
            { label: 'View Training Records', to: '/training' },
            { label: 'Start Another', onClick: reset },
          ]}
        />
      </WizardLayout>
    );
  }

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onSaveDraft={saveDraft}
      onNext={handleNext}
      onComplete={complete}
      onReset={reset}
    >
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 0.5, color: 'text.secondary' }}>
          Step {currentStepIndex + 1} of {totalSteps}
        </Typography>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
          {currentStep.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {getStepDescription(currentStep.id)}
        </Typography>

        {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}

        {renderFormFields(currentStep.id)}
      </Paper>
    </WizardLayout>
  );
};

function getStepDescription(stepId: string): string {
  const descriptions: Record<string, string> = {
    details: 'Capture training intervention details including employee, course, and schedule',
    seta: 'Enter SETA reporting fields including OFOC code and intervention classification',
    funding: 'Specify funding source and cost for B-BBEE compliance and SETA reporting',
    review: 'Review all details before submitting training intervention record',
  };
  return descriptions[stepId] || '';
}